import {
  configureStore,
  type EnhancedStore,
  type StoreEnhancer,
  type ThunkDispatch,
  type ThunkMiddleware,
  type Tuple,
  type UnknownAction,
} from '@reduxjs/toolkit';
import { App } from 'antd';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router';
import createSagaMiddleware, { type SagaMiddleware } from 'redux-saga';

import LayoutContainer from './containers/layout-container';
import Notification from './notification';
import rootReducer, { type RootState } from './reducers';
import { getSocket, mySaga } from './saga';
import Socket from './socket';
import { ThemeProvider } from './theme/theme-provider';

const sagaMiddleware: SagaMiddleware<object> = createSagaMiddleware();

const preloadedState: string | undefined = window.__PRELOADED_STATE__;
delete window.__PRELOADED_STATE__;

const store: EnhancedStore<
  RootState,
  UnknownAction,
  Tuple<
    [
      StoreEnhancer<{
        dispatch: ThunkDispatch<RootState, undefined, UnknownAction>;
      }>,
      StoreEnhancer,
    ]
  >
> = configureStore({
  reducer: rootReducer,
  preloadedState,
  middleware: (
    getDefaultMiddleware,
  ): Tuple<
    [ThunkMiddleware<RootState, UnknownAction>, SagaMiddleware<object>]
  > =>
    getDefaultMiddleware({
      serializableCheck: { warnAfter: 128 },
      immutableStateInvariant: { warnAfter: 128 },
    }).concat(sagaMiddleware),
});

sagaMiddleware.run(mySaga);

const socket = new Socket(getSocket(), store);

socket.requests();
socket.subscribe();

const element: HTMLElement | null = document.getElementById('admin');

if (element) {
  createRoot(element).render(
    <BrowserRouter>
      <Provider store={store}>
        <ThemeProvider>
          <App>
            <Notification socket={socket}>
              <LayoutContainer socket={socket} />
            </Notification>
          </App>
        </ThemeProvider>
      </Provider>
    </BrowserRouter>,
  );
}
